import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { MapPinOff, X } from "lucide-react";
import { useGeolocation } from "@/hooks/useGeolocation";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEY = "woma_last_position";
const EVT = "woma-position";

const MAX_AGE = 30 * 60 * 1000;

export const getLastPosition = () => {
  try {
    const p = JSON.parse(localStorage.getItem(KEY));
    return p && Date.now() - (p.ts || 0) < MAX_AGE ? p : null;
  } catch {
    return null;
  }
};

// Position GPS partagée par toute l'application (pré-remplissage départ, prestataires proches…)
export function useLastPosition() {
  const [pos, setPos] = useState(getLastPosition);
  useEffect(() => {
    const sync = () => setPos(getLastPosition());
    window.addEventListener(EVT, sync);
    return () => window.removeEventListener(EVT, sync);
  }, []);
  return pos;
}

const dist = (a, b) => {
  const dx = (a.lat - b.lat) * 111000;
  const dy = (a.lng - b.lng) * 111000 * Math.cos((a.lat * Math.PI) / 180);
  return Math.hypot(dx, dy);
};

/**
 * Capte la position dès l'ouverture (utilisateur connecté), la suit en temps réel tant que l'app est
 * au premier plan, la partage localement et la synchronise côté serveur (≥ 60 s ou ≥ 100 m).
 * Les livreurs ont leur propre suivi adaptatif sur leur tableau de bord.
 */
export const GeoProvider = ({ user }) => {
  const isCourier = (user?.active_role || user?.role) === "courier";
  const { position, status, request } = useGeolocation({ watch: true, enabled: !!user && !isCourier, mode: "idle" });
  const last = useRef({ ts: 0, pos: null });
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!position) return;
    localStorage.setItem(KEY, JSON.stringify({ lat: position.lat, lng: position.lng, ts: Date.now() }));
    window.dispatchEvent(new Event(EVT));
    const due = Date.now() - last.current.ts > 60000 || !last.current.pos || dist(last.current.pos, position) > 100;
    const isClient = (user?.roles || [user?.role]).includes("client");
    if (due && isClient && localStorage.getItem("auth_token")) {
      last.current = { ts: Date.now(), pos: position };
      axios.put(`${API}/users/me/location`, { latitude: position.lat, longitude: position.lng, role: "client" }).catch(() => {});
    }
  }, [position, user]);

  if (!user || isCourier || dismissed || !["denied", "unsupported"].includes(status)) return null;
  return (
    <div className="fixed bottom-24 inset-x-3 z-[1000] bg-[#7c2d12] text-white px-3 py-2 rounded-2xl flex items-center gap-2 sm:max-w-md sm:left-auto shadow-lg" data-testid="geo-denied-banner">
      <MapPinOff size={16} className="flex-shrink-0" />
      <p className="text-[11px] leading-snug flex-1 min-w-0">
        Activez la localisation pour profiter pleinement de WÔMA : adresse de départ automatique, prestataires de votre quartier et suivi en temps réel.
      </p>
      {status === "denied" && (
        <button type="button" onClick={request} className="text-[11px] font-black uppercase tracking-widest bg-white/15 rounded-full px-2.5 py-1 shrink-0" data-testid="geo-banner-retry-btn">Réessayer</button>
      )}
      <button type="button" onClick={() => setDismissed(true)} aria-label="Fermer" data-testid="geo-banner-close-btn"><X size={16} /></button>
    </div>
  );
};
